import { NavLink, Outlet, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchFeatureSwitches } from '@/api/client'
import {
  Bell,
  Calendar,
  CalendarX2,
  Eye,
  Layers,
  ListTree,
  Receipt,
  Settings,
  ArrowDownUp,
  ScrollText,
  SlidersHorizontal,
  Sparkles,
  Users,
  Building2,
  MailWarning,
  Bot,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { AdminTone } from '@/features/admin/AdminSection'

type NavItem = {
  to: string
  label: string
  icon: LucideIcon
  tone: AdminTone
  group: string
}

const NAV: NavItem[] = [
  { to: 'stages', label: 'Stages', icon: Layers, tone: 'azure', group: 'Forecast model' },
  { to: 'views', label: 'Forecast views', icon: Eye, tone: 'indigo', group: 'Forecast model' },
  { to: 'years', label: 'Forecast years', icon: Calendar, tone: 'teal', group: 'Forecast model' },
  {
    to: 'drivers',
    label: 'Forecast drivers',
    icon: ListTree,
    tone: 'teal',
    group: 'Forecast model',
  },
  {
    to: 'custom-fields',
    label: 'Custom fields',
    icon: Sparkles,
    tone: 'indigo',
    group: 'Forecast model',
  },
  {
    to: 'revenue-rules',
    label: 'Revenue rules',
    icon: Receipt,
    tone: 'amber',
    group: 'Forecast model',
  },
  {
    to: 'hygiene',
    label: 'Hygiene checks',
    icon: SlidersHorizontal,
    tone: 'amber',
    group: 'Quality',
  },
  { to: 'alerts', label: 'Alert rules', icon: Bell, tone: 'coral', group: 'Quality' },
  {
    to: 'closed-months',
    label: 'Closed months',
    icon: CalendarX2,
    tone: 'coral',
    group: 'Quality',
  },
  {
    to: 'config-transfer',
    label: 'Config transfer',
    icon: ArrowDownUp,
    tone: 'slate',
    group: 'Operations',
  },
  { to: 'access-log', label: 'Access log', icon: ScrollText, tone: 'slate', group: 'Operations' },
  { to: 'ai-log', label: 'AI turn log', icon: Bot, tone: 'indigo', group: 'Operations' },
  { to: 'customers', label: 'Customers', icon: Building2, tone: 'emerald', group: 'People' },
  { to: 'users', label: 'Access list', icon: Users, tone: 'emerald', group: 'People' },
  {
    to: 'email-failures',
    label: 'Email failures',
    icon: MailWarning,
    tone: 'rose',
    group: 'Operations',
  },
  { to: 'system', label: 'System', icon: Settings, tone: 'slate', group: 'Operations' },
]

function groupNav(items: NavItem[]) {
  const groups: { name: string; items: NavItem[] }[] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    if (last && last.name === item.group) {
      last.items.push(item)
    } else {
      groups.push({ name: item.group, items: [item] })
    }
  }
  return groups
}

export function AdminIndexRedirect() {
  return <Navigate to="stages" replace />
}

export function AdminLayout() {
  const switchesQuery = useQuery({
    queryKey: ['config', 'feature-switches'],
    queryFn: fetchFeatureSwitches,
  })
  const phase2Ui = switchesQuery.data?.feature_switches?.phase2_ui === true
  const navItems = NAV.filter((item) => item.to !== 'ai-log' || phase2Ui)
  const groups = groupNav(navItems)

  return (
    <section className="admin-shell hub-page">
      <div className="admin-shell__body">
        <nav className="admin-nav" aria-label="Admin sections">
          <div className="admin-nav__brand">
            <span className="admin-nav__brand-mark" aria-hidden />
            <div>
              <p className="admin-nav__brand-title">Admin</p>
              <p className="admin-nav__brand-sub">Configuration</p>
            </div>
          </div>

          <div className="admin-nav__groups">
            {groups.map((group) => (
              <div key={group.name} className="admin-nav__group">
                <p className="admin-nav__group-label">{group.name}</p>
                <ul className="admin-nav__list">
                  {group.items.map(({ to, label, icon: Icon, tone }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `admin-nav__link admin-nav__link--${tone}${
                            isActive ? ' admin-nav__link--active' : ''
                          }`
                        }
                        end={false}
                      >
                        <span className="admin-nav__icon" aria-hidden>
                          <Icon size={14} strokeWidth={2.25} />
                        </span>
                        <span>{label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <main className="admin-shell__content">
          <Outlet />
        </main>
      </div>
    </section>
  )
}
